import { FC, useCallback, useEffect, useContext, useMemo } from 'react';
import { Box, Select } from '@robocorp/components';
import { useTranslation } from 'react-i18next';

import { WorkItemState } from '@robocorp/robocloud-orchestrator-interface';

import { VariablesContext } from '../context';

import { defaultWorkItemsData, useWorkItemsLazyQuery } from '~/queries';

export const SelectWorkItem: FC = () => {
  const {
    endpoint,
    variables: { processId, workItemId },
    setVariables,
  } = useContext(VariablesContext);
  const { t } = useTranslation();

  const [
    { data: { data: workItems } = defaultWorkItemsData, isLoading: isLoadingWorkItems },
    getWorkItems,
  ] = useWorkItemsLazyQuery();

  useEffect(() => {
    if (!processId) return;
    getWorkItems({ scope: 'process', processId });
  }, [processId]);

  const processWorkItems = useMemo(() => {
    if (!workItems) {
      return [];
    }

    let items = workItems;

    if (endpoint.includes('/{fileId}')) {
      items = workItems.filter((item) => item.files && item.files.length > 0);
    }
    if (endpoint.includes('/retry')) {
      items = workItems.filter((item) => item.state === WorkItemState.Failed);
    }
    if (endpoint.includes('/complete')) {
      items = workItems.filter((item) => item.state === WorkItemState.Pending);
    }

    return items.map(({ id }) => {
      return { label: id, value: id };
    });
  }, [endpoint, workItems]);

  useEffect(() => {
    setVariables({ workItemId: processWorkItems.length ? processWorkItems[0].value : undefined });
  }, [processWorkItems]);

  const onChange = useCallback(
    (id: string) => {
      setVariables({ workItemId: id });
    },
    [setVariables],
  );

  let placeholder = t('noWorkItems');

  if (endpoint.includes('/{fileId}')) {
    placeholder = t('noWorkItemsWithFiles');
  }
  if (endpoint.includes('/retry')) {
    placeholder = t('noFailedWorkItems');
  }
  if (endpoint.includes('/complete')) {
    placeholder = t('noFailedWorkItems');
  }
  if (isLoadingWorkItems) {
    placeholder = t('loading');
  }

  return !endpoint.includes('workItemId') ? null : (
    <Box mb={20}>
      <Select
        label={t('chooseWorkItem')}
        items={processWorkItems}
        onChange={onChange}
        disabled={isLoadingWorkItems || !processId || !processWorkItems.length}
        value={workItemId}
        placeholder={processWorkItems.length ? `${t('choose')}...` : placeholder}
      />
    </Box>
  );
};
